import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Clock,
  Dumbbell,
  ChevronLeft,
  ChevronRight,
  ArrowRight
} from 'lucide-react';

import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';


const DIAS = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado'
];


const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro'
];


function dataLocal(data) {
  if (!data) return null;

  const partes = String(data).split('-');

  if (partes.length !== 3) return null;

  const ano = Number(partes[0]);
  const mes = Number(partes[1]);
  const dia = Number(partes[2]);

  if (!ano || !mes || !dia) return null;

  return new Date(ano, mes - 1, dia);
}


function chaveHoje() {
  const agora = new Date();

  const ano = agora.getFullYear();
  const mes = String(
    agora.getMonth() + 1
  ).padStart(2, '0');

  const dia = String(
    agora.getDate()
  ).padStart(2, '0');

  return `${ano}-${mes}-${dia}`;
}


function tituloData(data) {
  const d = dataLocal(data);

  if (!d) return data || '';

  return DIAS[d.getDay()];
}


function dataCompleta(data) {
  const d = dataLocal(data);

  if (!d) return '';

  return `${String(d.getDate()).padStart(2, '0')} de ${
    MESES[d.getMonth()]
  } de ${d.getFullYear()}`;
}


function dataCurta(data) {
  const d = dataLocal(data);

  if (!d) return '';

  return `${String(d.getDate()).padStart(2, '0')}/${String(
    d.getMonth() + 1
  ).padStart(2, '0')}`;
}


function horarioTreino(treino) {
  const inicio = treino?.horario || '';
  const fim = treino?.horario_fim || '';

  if (inicio && fim) {
    return `${inicio} – ${fim}`;
  }

  return inicio || 'Horário não informado';
}


export default function ProfessorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [treinos, setTreinos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [indiceData, setIndiceData] = useState(0);


  useEffect(() => {
    let ativo = true;

    Promise.all([
      api.get('/gestor/treinos'),
      api.get('/gestor/turmas')
    ])
      .then(([treinosRes, turmasRes]) => {
        if (!ativo) return;

        setTreinos(
          Array.isArray(treinosRes.data)
            ? treinosRes.data
            : []
        );

        setTurmas(
          Array.isArray(turmasRes.data)
            ? turmasRes.data
            : []
        );
      })
      .catch(() => {
        if (!ativo) return;

        setErro(
          'Não foi possível carregar sua agenda de treinos.'
        );
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });

    return () => {
      ativo = false;
    };
  }, []);


  const turmasPorId = useMemo(() => {
    const mapa = {};

    turmas.forEach(turma => {
      if (turma?.id) {
        mapa[turma.id] = turma;
      }
    });

    return mapa;
  }, [turmas]);


  const agenda = useMemo(() => {
    const hoje = chaveHoje();
    const grupos = {};

    treinos
      .filter(treino => {
        return (
          treino?.data
          && treino.data >= hoje
        );
      })
      .forEach(treino => {
        if (!grupos[treino.data]) {
          grupos[treino.data] = [];
        }

        grupos[treino.data].push(treino);
      });

    return Object.keys(grupos)
      .sort()
      .map(data => ({
        data,
        treinos: grupos[data].sort((a, b) => {
          return String(
            a?.horario || ''
          ).localeCompare(
            String(b?.horario || '')
          );
        })
      }));
  }, [treinos]);


  useEffect(() => {
    if (
      agenda.length > 0
      && indiceData >= agenda.length
    ) {
      setIndiceData(0);
    }
  }, [agenda, indiceData]);


  const diaAtual = agenda[indiceData];


  if (loading) {
    return (
      <div
        className="space-y-6"
        data-testid="professor-dashboard-loading"
      >
        <Skeleton className="h-20 bg-[#141414]" />
        <Skeleton className="h-64 bg-[#141414]" />
      </div>
    );
  }


  return (
    <div
      className="space-y-6 animate-fadeIn"
      data-testid="professor-dashboard"
    >
      <div>
        <p className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500">
          Agenda do professor
        </p>

        <h1 className="text-3xl md:text-4xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white mt-1">
          Olá, {user?.name?.split(' ')[0] || 'Professor'}
        </h1>

        <p className="text-sm text-zinc-500 mt-1">
          Veja seu próximo dia de treinos.
        </p>
      </div>


      {erro && (
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-5">
            <p className="text-sm text-red-400">
              {erro}
            </p>
          </CardContent>
        </Card>
      )}


      {!erro && !diaAtual && (
        <Card
          className="bg-[#141414] border-[#27272A]"
          data-testid="professor-sem-treinos"
        >
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#007AFF]/10 flex items-center justify-center">
              <CalendarDays className="w-7 h-7 text-[#007AFF]" />
            </div>

            <h2 className="text-xl font-bold text-white mt-4">
              Nenhum próximo treino agendado
            </h2>

            <p className="text-sm text-zinc-500 mt-2">
              Quando novos treinos forem cadastrados,
              eles aparecerão aqui.
            </p>
          </CardContent>
        </Card>
      )}


      {!erro && diaAtual && (
        <>
          <Card
            className="bg-[#141414] border-[#27272A] overflow-hidden"
            data-testid="professor-proximo-dia"
          >
            <div className="h-1 bg-[var(--cor-primaria,#007AFF)]" />

            <CardContent className="p-0">
              <div className="p-5 md:p-6 border-b border-[#27272A]">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                  <div>
                    <div className="flex items-center gap-2 text-[var(--cor-primaria,#007AFF)]">
                      <CalendarDays className="w-4 h-4" />

                      <span className="text-xs font-bold tracking-[0.16em] uppercase">
                        {indiceData === 0
                          ? 'Próximo dia de treino'
                          : 'Agenda de treinos'}
                      </span>
                    </div>

                    <h2 className="text-3xl md:text-4xl font-black font-['Barlow_Condensed'] uppercase text-white mt-2">
                      {tituloData(diaAtual.data)}
                    </h2>

                    <p className="text-sm text-zinc-500 mt-1">
                      {dataCompleta(diaAtual.data)}
                    </p>
                  </div>


                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={indiceData === 0}
                      onClick={() => {
                        setIndiceData(i =>
                          Math.max(0, i - 1)
                        );
                      }}
                      className="border-[#27272A] bg-[#0A0A0A]"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={
                        indiceData >= agenda.length - 1
                      }
                      onClick={() => {
                        setIndiceData(i =>
                          Math.min(
                            agenda.length - 1,
                            i + 1
                          )
                        );
                      }}
                      className="border-[#27272A] bg-[#0A0A0A]"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>


              <div className="divide-y divide-[#27272A]">
                {diaAtual.treinos.map(treino => {
                  const turma =
                    turmasPorId[treino.turma_id];

                  return (
                    <div
                      key={treino.id}
                      className="p-5 md:p-6 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                        <div className="flex gap-4">
                          <div className="w-12 h-12 shrink-0 rounded-xl bg-[var(--cor-primaria,#007AFF)]/10 flex items-center justify-center">
                            <Dumbbell className="w-5 h-5 text-[var(--cor-primaria,#007AFF)]" />
                          </div>

                          <div>
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Clock className="w-4 h-4" />

                              <span className="text-sm font-semibold">
                                {horarioTreino(treino)}
                              </span>
                            </div>

                            <h3 className="text-lg font-bold text-white mt-1">
                              {turma?.nome
                                || treino?.nome
                                || 'Treino'}
                            </h3>

                            {treino?.nome
                              && turma?.nome
                              && treino.nome !== turma.nome && (
                                <p className="text-xs text-zinc-500 mt-1">
                                  {treino.nome}
                                </p>
                              )}
                          </div>
                        </div>


                        <Button
                          type="button"
                          onClick={() =>
                            navigate('/gestor/treinos')
                          }
                          className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90"
                        >
                          Abrir treinos
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>


          <div className="flex items-center justify-between gap-4 text-xs text-zinc-500">
            <span>
              {diaAtual.treinos.length}{' '}
              {diaAtual.treinos.length === 1
                ? 'treino'
                : 'treinos'}{' '}
              em {dataCurta(diaAtual.data)}
            </span>

            <span>
              {indiceData + 1} de {agenda.length}{' '}
              {agenda.length === 1
                ? 'dia com treino'
                : 'dias com treino'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
